import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { Entity, WorldMap } from './protocol';
import type { Direction } from './prediction';
import { HEIGHT } from './movement';

// Second Life (forward X, up Z) → Offworld (forward Z, up Y).
// These are root-relative offsets, not ground-relative heights. See docs/camera.md.
export const CAMERA_OFFSET = new THREE.Vector3(0, .75, -3);
export const FOCUS_OFFSET = new THREE.Vector3(0, 1, 1);
export const REAR_VIEW_FOV = 60;
export type CameraPreset = 'rear' | 'front' | 'side' | 'mouselook';
export const CAMERA_PRESETS = {
  rear: { camera: CAMERA_OFFSET, focus: FOCUS_OFFSET },
  front: { camera: new THREE.Vector3(0, 0, 2.2), focus: new THREE.Vector3(0, 0, 0) },
  side: { camera: new THREE.Vector3(.7, .5, -1), focus: new THREE.Vector3(.7, 1, 1.5) },
};
const ROOT_HEIGHT = HEIGHT / 2;
const HEAD_OFFSET = new THREE.Vector3(0, 1, 0);
const EYE_HEIGHT = HEIGHT - .15;
const up = new THREE.Vector3(0, 1, 0);

export function avatarHeading(entity: Entity): number {
  const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(new THREE.Quaternion().fromArray(entity.rotation));
  return Math.atan2(forward.x, forward.z);
}

/** Firestorm's preset anchors; rear reset leaves a 5% margin below the feet. */
export function cameraPresetPose(entity: Entity, preset: Exclude<CameraPreset, 'mouselook'>, verticalFov = REAR_VIEW_FOV): { position: THREE.Vector3; target: THREE.Vector3 } {
  const feet = new THREE.Vector3(entity.position.x, entity.position.y, entity.position.z);
  const root = feet.clone().addScaledVector(up, ROOT_HEIGHT), heading = avatarHeading(entity);
  const offsets = CAMERA_PRESETS[preset];
  const target = offsets.focus.clone().applyAxisAngle(up, heading).add(root);
  const position = offsets.camera.clone().applyAxisAngle(up, heading).add(root).add(HEAD_OFFSET);
  if (preset !== 'rear') return { position, target };
  const forward = target.clone().sub(position).normalize();
  const screenUp = up.clone().addScaledVector(forward, -up.dot(forward)).normalize();
  const feetFromTarget = feet.clone().sub(target);
  const bottomSlope = .9 * Math.tan(THREE.MathUtils.degToRad(verticalFov / 2));
  const framedDistance = -feetFromTarget.dot(screenUp) / bottomSlope - feetFromTarget.dot(forward);
  // Preserve the preset's downward pitch and focus; only increase reset distance.
  const distance = Math.max(position.distanceTo(target), framedDistance);
  position.copy(target).addScaledVector(forward, -distance);
  return { position, target };
}

/** Forward/back intent projected onto the ground; backing up preserves facing. */
export function cameraRelativeMovement(forward: number, heading: number): Direction {
  const x = Math.sin(heading) * forward, z = Math.cos(heading) * forward;
  return { x: Math.abs(x) < 1e-10 ? 0 : x, z: Math.abs(z) < 1e-10 ? 0 : z, yaw: Math.atan2(Math.sin(heading), Math.cos(heading)) };
}

/** Keep the camera on the avatar's side of solid walls, with near-plane clearance. */
export function unobstructedCamera(anchor: THREE.Vector3, desired: THREE.Vector3, map: WorldMap | null): THREE.Vector3 {
  const position = desired.clone();
  position.y = Math.max(position.y, (map?.bounds.min.y ?? 0) + .2);
  if (!map) return position;
  const direction = position.clone().sub(anchor), distance = direction.length();
  if (distance < .001) return position;
  const ray = new THREE.Ray(anchor, direction.divideScalar(distance));
  let allowed = distance;
  for (const obstacle of map.obstacles) {
    const box = new THREE.Box3(new THREE.Vector3(obstacle.min.x, obstacle.min.y, obstacle.min.z), new THREE.Vector3(obstacle.max.x, obstacle.max.y, obstacle.max.z)).expandByScalar(.15);
    // A camera anchor at a wall's edge must not jump to that wall's far side.
    if (box.containsPoint(anchor)) return anchor.clone();
    const hit = ray.intersectBox(box, new THREE.Vector3());
    if (hit) allowed = Math.min(allowed, Math.max(0, anchor.distanceTo(hit) - .01));
  }
  return ray.at(allowed, position);
}

export class WorldCamera {
  private orbit: OrbitControls;
  private rig: THREE.PerspectiveCamera;
  private subject: Entity | undefined;
  private subjectID = '';
  private heading = 0;
  private desired = new THREE.Vector3();
  private target = new THREE.Vector3();
  private anchor = new THREE.Vector3();
  private map: WorldMap | null = null;
  private mode: CameraPreset = 'rear';
  private pitch = 0;
  private events = new AbortController();
  private pointer: { id: number; x: number; y: number } | null = null;
  private locked = false;

  constructor(private camera: THREE.PerspectiveCamera, private canvas: HTMLCanvasElement) {
    // Orbit an unclipped camera so wall avoidance never changes the chosen zoom.
    this.rig = camera.clone();
    this.orbit = new OrbitControls(this.rig, canvas);
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = .12;
    this.orbit.enablePan = false;
    this.orbit.minDistance = 1.75;
    this.orbit.maxDistance = 20;
    this.orbit.minPolarAngle = .12;
    this.orbit.maxPolarAngle = Math.PI * .54;
    this.orbit.rotateSpeed = .65;
    this.orbit.zoomSpeed = .8;
    this.orbit.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
    this.orbit.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };
    this.desired.copy(camera.position);
    const options = { signal: this.events.signal };
    canvas.addEventListener('pointerdown', event => {
      if (this.mode !== 'mouselook') return;
      canvas.focus(); this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      canvas.setPointerCapture(event.pointerId);
    }, options);
    document.addEventListener('pointermove', event => {
      if (this.mode !== 'mouselook') return;
      let dx = 0, dy = 0;
      if (document.pointerLockElement === canvas) { dx = event.movementX; dy = event.movementY; }
      else if (this.pointer?.id === event.pointerId) {
        dx = event.clientX - this.pointer.x; dy = event.clientY - this.pointer.y;
        this.pointer.x = event.clientX; this.pointer.y = event.clientY;
      }
      this.heading -= dx * .0025;
      this.pitch = THREE.MathUtils.clamp(this.pitch - dy * .0025, -Math.PI / 2 + .05, Math.PI / 2 - .05);
    }, options);
    const release = (): void => { this.pointer = null; };
    canvas.addEventListener('pointerup', release, options); canvas.addEventListener('pointercancel', release, options);
    window.addEventListener('blur', release, options);
    document.addEventListener('pointerlockchange', () => {
      const wasLocked = this.locked; this.locked = document.pointerLockElement === canvas;
      if (wasLocked && !this.locked && this.mode === 'mouselook') this.select('rear');
      if (this.locked && this.mode !== 'mouselook') document.exitPointerLock();
    }, options);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.mode === 'mouselook') { event.preventDefault(); this.select('rear'); canvas.focus(); }
    }, { ...options, capture: true });
  }

  get preset(): CameraPreset { return this.mode; }
  releasePointerLock(): void {
    this.locked = false; this.pointer = null;
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }
  set enabled(enabled: boolean) { this.orbit.enabled = enabled && this.mode !== 'mouselook'; }

  select(preset: CameraPreset): void {
    this.mode = preset; this.pointer = null;
    // Keep the local body rendered in first person, with clearance for the face.
    this.camera.near = preset === 'mouselook' ? .2 : .1; this.camera.updateProjectionMatrix();
    this.orbit.enabled = preset !== 'mouselook';
    if (preset !== 'mouselook' && document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.reset();
    if (preset === 'mouselook' && this.subject) {
      this.canvas.focus();
      // Pointer lock is optional (e.g. touch/embedded browsers); drag-look remains available.
      try { void this.canvas.requestPointerLock()?.catch(() => {}); } catch { /* drag-look fallback */ }
    }
  }

  movement(forward: number): Direction {
    return cameraRelativeMovement(forward, this.mode === 'mouselook' ? this.heading : this.orbit.getAzimuthalAngle() + Math.PI);
  }

  turn(right: number, dt: number): void {
    if (!this.subject || !right) return;
    if (this.mode === 'mouselook') { this.heading -= right * Math.PI * .75 * dt; return; }
    this.rig.position.sub(this.orbit.target).applyAxisAngle(up, -right * Math.PI * .75 * dt).add(this.orbit.target);
    this.orbit.update(); this.present();
  }

  reset(): void {
    if (!this.subject) return;
    // Drain previous drag momentum before assigning the exact default view.
    this.orbit.enableDamping = false; this.orbit.update(); this.orbit.enableDamping = true;
    this.heading = avatarHeading(this.subject);
    if (this.mode === 'mouselook') { this.pitch = 0; this.presentMouselook(); return; }
    const pose = cameraPresetPose(this.subject, this.mode, this.camera.fov);
    this.orbit.target.copy(pose.target);
    this.rig.position.copy(pose.position);
    this.orbit.update(); this.present();
  }

  zoom(factor: number): void {
    if (!this.subject || this.mode === 'mouselook') return;
    const offset = this.rig.position.clone().sub(this.orbit.target);
    const distance = THREE.MathUtils.clamp(offset.length() * factor, this.orbit.minDistance, this.orbit.maxDistance);
    this.rig.position.copy(this.orbit.target).add(offset.setLength(distance));
    this.orbit.update(); this.present();
  }

  update(subject: Entity | undefined, map: WorldMap | null, dt: number): void {
    this.subject = subject; this.map = map;
    if (!subject) { this.subjectID = ''; if (this.mode === 'mouselook') this.select('rear'); return; }
    if (subject.id !== this.subjectID) { this.subjectID = subject.id; this.reset(); return; }
    if (this.mode === 'mouselook') { this.presentMouselook(); return; }
    // Heading belongs to the view: mouse orbit or explicit A/D turning. Avatar
    // facing follows movement intent, rather than feeding back into camera yaw.
    this.heading = this.orbit.getAzimuthalAngle() + Math.PI;
    this.target.copy(CAMERA_PRESETS[this.mode].focus).applyAxisAngle(up, this.heading).add(new THREE.Vector3(subject.position.x, subject.position.y + ROOT_HEIGHT, subject.position.z));
    // Translate with the presented avatar, preserving the user's orbit and zoom.
    this.rig.position.sub(this.orbit.target).add(this.target);
    this.orbit.target.copy(this.target);
    this.orbit.update(dt); this.present();
  }

  private present(): void {
    if (!this.subject) return;
    this.rig.position.y = Math.max(this.rig.position.y, (this.map?.bounds.min.y ?? 0) + .2);
    this.desired.copy(this.rig.position);
    this.anchor.set(this.subject.position.x, this.subject.position.y + ROOT_HEIGHT + HEAD_OFFSET.y, this.subject.position.z);
    this.camera.position.copy(unobstructedCamera(this.anchor, this.desired, this.map));
    this.camera.lookAt(this.orbit.target);
  }

  private presentMouselook(): void {
    if (!this.subject) return;
    this.anchor.set(this.subject.position.x, this.subject.position.y + EYE_HEIGHT, this.subject.position.z);
    // Lean forward around the neck as we look down, exposing the chest instead
    // of looking straight through the torso. Level/upward views stay at eye level.
    const lean = .28 * Math.max(0, -Math.sin(this.pitch));
    this.desired.set(Math.sin(this.heading) * lean, 0, Math.cos(this.heading) * lean).add(this.anchor);
    this.camera.position.copy(unobstructedCamera(this.anchor, this.desired, this.map));
    this.target.set(Math.sin(this.heading) * Math.cos(this.pitch), Math.sin(this.pitch), Math.cos(this.heading) * Math.cos(this.pitch)).add(this.camera.position);
    this.camera.lookAt(this.target);
  }

  get diagnostics() {
    const feet = this.subject && new THREE.Vector3(this.subject.position.x, this.subject.position.y, this.subject.position.z).project(this.camera);
    return { preset: this.mode, position: this.camera.position.toArray(), target: (this.mode === 'mouselook' ? this.target : this.orbit.target).toArray(), distance: this.mode === 'mouselook' ? 0 : this.desired.distanceTo(this.orbit.target), following: this.subjectID, feetScreenY: feet && this.mode !== 'mouselook' ? (1 - feet.y) / 2 : null };
  }

  dispose(): void {
    this.events.abort();
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.orbit.dispose();
  }
}
